import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../../../config/axiosInstance';

// Carga turnos habilitados (principales y otros), dotación por defecto y reglas de una estación (un solo
// endpoint propio de dotación, sin depender de calendarios ni turnos), y agrupa
// las áreas igual que la pantalla de turnos: el valor de un área se escribe en su
// "posición primaria" (la workarea de menor id) y el resto queda en 0.
export default function useStaffingData(stationId) {
  const [loading, setLoading] = useState(false);
  const [workshifts, setWorkshifts] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [rules, setRules] = useState([]);

  const refresh = useCallback(async () => {
    if (!stationId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`/station/${stationId}/staffing/overview`);
      setWorkshifts(data?.workshifts || []);
      setQuantities(data?.quantities || []);
      setRules(data?.rules || []);
    } catch (e) {
      console.error(e);
      setWorkshifts([]); setQuantities([]); setRules([]);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [stationId]);

  useEffect(() => {
    setWorkshifts([]); setQuantities([]); setRules([]);
    refresh().catch(() => {});
  }, [refresh]);

  const sortByStart = (list) => [...list].sort((a, b) => String(a.hour_from || '').localeCompare(String(b.hour_from || '')));

  // Los principales van primero; el resto de los turnos se muestra aparte como opcional.
  // (Un turno solo se valida cuando su dotación del día es mayor que 0, sea o no principal.)
  const principalWorkshifts = useMemo(
    () => sortByStart((workshifts || []).filter((w) => w.is_principal && w.enabled !== false)),
    [workshifts]
  );
  const otherWorkshifts = useMemo(
    () => sortByStart((workshifts || []).filter((w) => !w.is_principal && w.enabled !== false)),
    [workshifts]
  );

  const areas = useMemo(() => {
    const map = new Map();
    (quantities || []).forEach((q) => {
      if (q.id_position == null) return;
      if (!map.has(q.id_position)) {
        map.set(q.id_position, { id: q.id_position, name: q.position_name || `Área ${q.id_position}`, workareaIds: [] });
      }
      const entry = map.get(q.id_position);
      if (!entry.workareaIds.includes(q.workarea_id)) entry.workareaIds.push(q.workarea_id);
    });
    return Array.from(map.values())
      .map((a) => ({ ...a, workareaIds: [...a.workareaIds].sort((x, y) => x - y) }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [quantities]);

  const defaultFor = useCallback((area, workshiftId) => (
    (quantities || [])
      .filter((q) => area.workareaIds.includes(q.workarea_id) && q.workshift_id === workshiftId)
      .reduce((acc, q) => acc + Number(q.total || 0), 0)
  ), [quantities]);

  return { loading, refresh, principalWorkshifts, otherWorkshifts, areas, rules, defaultFor };
}
